/**
 * Lead sources: a fixed list of channels (so reports group cleanly), plus free-text detail for the
 * campaign, event or person. Common CRM practice: a picklist, the original source kept as first
 * recorded, and reports by source carried through to calls, clients and fees, not just lead counts.
 * Also: loss reasons from a fixed list, and a simple lead score for sorting.
 */
import type { AuditRow } from "../data/db.ts";
import { pipelineValue } from "./crm.ts";
import { isOpen, triage, type LeadRow } from "./leads.ts";

export const SOURCE_CHANNELS = [
  { key: "website", label: "Website (enquiry or fit check)" },
  { key: "google_search", label: "Google search (found us)" },
  { key: "google_ads", label: "Google Ads" },
  { key: "linkedin_outreach", label: "LinkedIn: our outreach" },
  { key: "linkedin_inbound", label: "LinkedIn: they messaged us" },
  { key: "cold_email", label: "Cold email" },
  { key: "cold_call", label: "Cold call" },
  { key: "client_referral", label: "Referral from a client" },
  { key: "partner", label: "Partner introduction" },
  { key: "event", label: "Event or trade show" },
  { key: "community", label: "Online community or group" },
  { key: "directory", label: "Directory or listing" },
  { key: "marketplace", label: "Marketplace or platform partner (Shopify, Amazon…)" },
  { key: "press", label: "Press, podcast or article" },
  { key: "other", label: "Other" },
] as const;

export type SourceChannel = (typeof SOURCE_CHANNELS)[number]["key"];
export const CHANNEL_LABEL = Object.fromEntries(SOURCE_CHANNELS.map((c) => [c.key, c.label])) as Record<SourceChannel, string>;
export const NOT_RECORDED = "not_recorded";

/** The channel a lead came from: as recorded, or worked out for website and partner leads, or not recorded. */
export function channelOf(l: Pick<LeadRow, "source_channel" | "source" | "origin">): SourceChannel | typeof NOT_RECORDED {
  if (l.source_channel && l.source_channel in CHANNEL_LABEL) return l.source_channel as SourceChannel;
  if (l.source === "enquiry" || l.source === "fit-check") return "website";
  if (l.origin === "partner") return "partner";
  return NOT_RECORDED;
}
export const channelLabel = (c: string): string => (c === NOT_RECORDED ? "Not recorded" : CHANNEL_LABEL[c as SourceChannel] ?? c);

/** A sensible starting channel for a lead added by hand, from how we met them. */
export const defaultChannel = (origin: LeadRow["origin"]): SourceChannel => (origin === "outbound" ? "linkedin_outreach" : origin === "partner" ? "partner" : "google_search");

const CONVERSATION = new Set(["replied", "call_booked", "call_done", "proposal_sent", "won"]);

export interface ChannelRow {
  channel: string;
  label: string;
  leads: number;
  conversations: number;
  calls: number;
  clients: number;
  /** Clients over leads, 0 to 1, or null with no leads */
  winRate: number | null;
  /** Agreed audit fees of the audits these leads became, in pence */
  feesWonPence: number;
  /** Audit fees open leads would bring, from their order band */
  openPipelinePence: number;
  /** Average days from first contact to becoming a client */
  avgDaysToWin: number | null;
}

/** Leads, conversations, calls, clients and fees for each channel, best first (clients, then win rate). */
export function channelReport(leads: LeadRow[], audits: Pick<AuditRow, "id" | "audit_fee_pence">[] = []): ChannelRow[] {
  const fee = new Map(audits.map((a) => [a.id, a.audit_fee_pence ?? 0]));
  const groups = new Map<string, LeadRow[]>();
  for (const l of leads) {
    const c = channelOf(l);
    groups.set(c, [...(groups.get(c) ?? []), l]);
  }
  return [...groups.entries()].map(([channel, ls]) => {
    const won = ls.filter((l) => l.status === "won");
    const days = won.filter((l) => l.won_at).map((l) => (Date.parse(l.won_at!) - Date.parse(l.received_at || l.created_at)) / 864e5).filter((d) => Number.isFinite(d) && d >= 0);
    return {
      channel, label: channelLabel(channel), leads: ls.length,
      conversations: ls.filter((l) => CONVERSATION.has(l.status) || l.replied_at).length,
      calls: ls.filter((l) => l.call_done_at || l.status === "won").length,
      clients: won.length,
      winRate: ls.length ? won.length / ls.length : null,
      feesWonPence: won.reduce((s, l) => s + (l.audit_id ? fee.get(l.audit_id) ?? 0 : 0), 0),
      openPipelinePence: pipelineValue(ls.filter((l) => isOpen(l.status))).pence,
      avgDaysToWin: days.length ? Math.round(days.reduce((a, b) => a + b, 0) / days.length) : null,
    };
  }).sort((a, b) => b.clients - a.clients || (b.winRate ?? 0) - (a.winRate ?? 0) || b.leads - a.leads);
}

// ---------- Why leads were lost

export const LOSS_REASONS = [
  "Not a fit (too small or wrong kind of business)",
  "Price",
  "Timing: not now",
  "No response",
  "Chose another provider",
  "Doing it themselves",
  "Their carrier or 3PL controls costs",
  "Asked not to be contacted",
  "Other",
] as const;

/** The reason part of a closed reason ("Price: wanted it cheaper" → "Price"). Free text that matches none counts as Other. */
export function lossReasonOf(closed: string | null | undefined): string | null {
  if (!closed) return null;
  return LOSS_REASONS.find((r) => closed === r || closed.startsWith(`${r}: `)) ?? "Other";
}

export function lossReport(leads: LeadRow[]): { reason: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const l of leads) {
    if (!["lost", "not_a_fit", "no_response"].includes(l.status)) continue;
    const r = lossReasonOf(l.closed_reason) ?? (l.status === "no_response" ? "No response" : l.status === "not_a_fit" ? LOSS_REASONS[0] : "Other");
    counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  return [...counts.entries()].map(([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count);
}

// ---------- Lead score (0 to 100), for sorting: fit first, then how keen and how engaged

export function leadScore(l: LeadRow, now = new Date()): number {
  if (!isOpen(l.status) || l.do_not_contact) return 0;
  const t = triage(l);
  let s = t.fit === "quote" ? 40 : t.fit === "audit" ? 35 : t.fit === "review" ? 20 : t.fit === "guide" ? 5 : 15;
  if (l.timing === "Now") s += 15; else if (l.timing && /month/i.test(l.timing)) s += 8;
  if (l.request === "call") s += 10;
  if (l.concerns?.includes("renewal")) s += 8;
  if (l.replied_at || ["replied", "call_booked", "call_done", "proposal_sent"].includes(l.status)) s += 10;
  if (l.status === "call_booked") s += 5;
  if (l.status === "call_done" || l.status === "proposal_sent") s += 12;
  const last = [l.last_contact_at, l.last_touch_at, l.replied_at, l.received_at].filter(Boolean).sort().pop();
  const quiet = last ? (now.getTime() - Date.parse(last)) / 864e5 : 0;
  if (quiet > 30) s -= 15; else if (quiet > 14) s -= 8;
  return Math.max(0, Math.min(100, Math.round(s)));
}

/** Tags typed as "Shopify, fashion,  trade show" → ["Shopify", "fashion", "trade show"], no repeats, at most 20. */
export function parseTags(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[,;\n]/)) {
    const t = raw.trim().replace(/\s+/g, " ").slice(0, 40);
    if (t && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  }
  return out.slice(0, 20);
}
